// Modules
import gsap from 'gsap'
import { ScrollTrigger as GsapScrollTrigger } from 'gsap/ScrollTrigger'
import { Draggable as GsapDraggable } from 'gsap/Draggable'
import Swiper, { Pagination as SwiperPagination } from 'swiper'

// Local dependencies
import '../scss/style.scss'
import EventBus from './_event-bus'
import Navigation from './_navigation'
import Parallaxer from './_parallaxer'
import ScrollTrigger from './_scroll-trigger'
import VideoPlayer from './_video-player'
import Carousel from './_carousel'
import SwipeZone from './_swipe-zone'
import Modal from './_modal'
import AgeGate from './_age-gate'

const App = class {
	sklikId = 1507290

	constructor() {
		this.gsap = gsap
		this.draggable = GsapDraggable
		this.SwipeZone = SwipeZone
		this.bus = new EventBus()
		this.store = {
			hasPreloadingAssets: !!document.getElementsByClassName('has-preloading-assets').length,
			assetsReady: false,
			modals: {}
		}
		this.sharedComponents = {
			navigation: new Navigation(this.store.hasPreloadingAssets),
			parallaxer: new Parallaxer(this.gsap),
			scrollTrigger: new ScrollTrigger()
		}
		this.ageGate = new AgeGate(this)

		this.init()

		document.addEventListener( 'DOMContentLoaded', () => this.onDOMLoad() )
		window.addEventListener( 'load', () => this.onPageLoad() )
	}

	init() {
		this.gsap.registerPlugin(GsapScrollTrigger)
		this.gsap.registerPlugin(GsapDraggable)

		this.bus.on('openModal', name => {
			const modalData = window.modals?.[name]
			if (!modalData) return

			if (!this.store.modals[name]) {
				this.store.modals[name] = new Modal(name, modalData.title, modalData.content, modalData.options)
			}

			this.store.modals[name].open()
		})

		this.setupAutoModals( Object.entries(window.modals).filter(it => it[1].options.autoDisplay) )

		this.setupTracking()
	}

	setupAutoModals(modals) {
		modals.forEach(it => {
			const [ name, data ] = it

			const pageUrls = data.options.pages || null
			const shouldBeDisplayed = !pageUrls || pageUrls.some(p => new URL(p).pathname === window.location.pathname)

			if (!shouldBeDisplayed) return

			const modal = new Modal(name, data.title, data.content, data.options)
			this.store.modals[name] = modal

			if (window.ageGatePassed) {
				// If age gate has already been passed, open the modal immediately
				modal.open(data.options.delay, data.options.once, true)
			} else {
				// Otherwise wait for the age gate to be passed
				this.bus.on('ageGatePassed', () => modal.open(data.options.delay, data.options.once, true))
			}
		})
	}

	setupTracking() {
		let evidonRetries = 0
		let sklikRetries = 0

		const setupSklik = consentGiven => {
			if (window.rc) {
				window.rc.retargetingHit({
					rtgId: this.sklikId,
					consent: consentGiven ? 1 : 0
				})
			}
			else if (sklikRetries < 20) {
				sklikRetries++
				setTimeout(() => setupSklik(consentGiven), 100)
			}
		}

		setupSklik(true)
	}

	onDOMLoad() {
		// Adjust headings line height for accented characters
		document.querySelectorAll('h1, h2').forEach(it => {
			const hasAccentedChars = /[áéíóúýčďěňřšťžů]/i.test(it.textContent)
			if (hasAccentedChars) it.classList.add('has-accented-chars')
		})

		// Register scroll animations
		const textBlocks = document.getElementsByClassName('text-block')
		this.sharedComponents.scrollTrigger.registerElements(textBlocks, { offset: '-150px' })

		const imageAndTextBlocks = document.getElementsByClassName('image-and-text')
		this.sharedComponents.scrollTrigger.registerElements(imageAndTextBlocks, { offset: '-150px' })

		const sections = document.querySelectorAll('.section__background *[data-scroll-state]')
		this.sharedComponents.scrollTrigger.registerElements(sections, { offset: '-300px' })

		const headingUnderlines = document.querySelectorAll('h1 u, h2 u')
		this.sharedComponents.scrollTrigger.registerElements(headingUnderlines, { offset: '-150px' })

		const restOfScrollElements = document.querySelectorAll('*[data-scroll-state]')
		this.sharedComponents.scrollTrigger.registerElements(restOfScrollElements, { offset: '-180px' })

		// Register video controls
		const videos = document.querySelectorAll('.video')
		videos.forEach(el => { new VideoPlayer(el) })

		// Register carousels
		const carousels = document.querySelectorAll('.carousel')
		carousels.forEach(el => { new Carousel(el, Swiper, SwiperPagination) })

		// Modals
		const modalLinks = document.querySelectorAll('a[href^="#modal-"]')
		modalLinks.forEach(it => {
			it.addEventListener('click', (e) => {
				e.preventDefault()
				const name = e.target.getAttribute('href').split('#modal-')[1]
				this.bus.emit('openModal', name)
			})
		})

		// Forms
		window.addEventListener('click', (e) => {
			e.target.nodeName === 'INPUT' &&
			e.target.getAttribute('type') === 'date' &&
			e.target.classList.add('touched')
		})
	}

	onPageLoad() {
		const parallaxWrappers = document.getElementsByClassName('parallax-wrapper')
		this.sharedComponents.parallaxer.registerElements(parallaxWrappers)
	}
}

window.app = new App()
